// A page's history is its doc: Loro keeps every edit, so any version can
// be seen again. A version is marked at the end of an editing session,
// and lists who edited in it. Returning to one is a new edit that makes
// the page match it, so nothing after it is lost.
// meets: history/versions, history/file, ai/named
import { decodeFrontiers, encodeFrontiers, LoroDoc, LoroList, type OpId } from "loro-crdt";
import { Page, type NodeJSON, type Spec, type Target } from "./doc";
import { parse, print } from "./textform";

export type Version = { n: number; at: number; frontiers: OpId[]; authors: string[] };

export const toSpec = (n: NodeJSON): Spec => ({
  id: n.id,
  type: n.type,
  props: n.props,
  style: n.style,
  ...(n.text ? { delta: n.text } : {}),
  children: n.children.map(toSpec),
});

export const textOf = (p: Page) => print(p.roots(), p.tokens.toJSON() as Record<string, string>);

export const targetOf =(p: Page): Target => ({ tokens: p.tokens.toJSON() as Record<string, string>, nodes: p.roots().map(toSpec) });

export class History {
  readonly list: LoroList;
  constructor(private page: Page) {
    this.list = page.doc.getList("versions");
  }

  // Mark the page as it is now as a version, unless nothing changed since
  // the last one.
  mark(): boolean {
    const doc = this.page.doc;
    const last = this.versions().at(-1);
    // Comments, names and marks since the last version don't make a new
    // one; only a change to the page does.
    if (last && textOf(this.at(last)) === textOf(this.page)) return false;
    this.list.push({ f: Array.from(encodeFrontiers(doc.frontiers())), at: Date.now() } as never);
    doc.commit({ origin: "aside:version" });
    return true;
  }

  versions(): Version[] {
    const doc = this.page.doc;
    const raw = this.list.toJSON() as { f: number[]; at: number }[];
    const people = this.page.people.toJSON() as Record<string, string>;
    let before = new Map<string, number>();
    return raw.map((r, n) => {
      const frontiers = decodeFrontiers(new Uint8Array(r.f));
      let vv: Map<string, number>;
      try {
        vv = doc.frontiersToVV(frontiers).toJSON();
      } catch {
        vv = before;
      }
      const authors: string[] = [];
      for (const [peer, counter] of vv) if (counter > (before.get(peer) ?? 0)) authors.push(people[peer] ?? (n === 0 ? "The starting page" : "Someone"));
      before = vv;
      return { n, at: r.at, frontiers, authors: [...new Set(authors)] };
    });
  }

  // The page as it was: a separate, read-only copy.
  at(v: Version): Page {
    const doc = this.page.doc.forkAt(v.frontiers);
    return new Page(doc);
  }

  // Return to a version: a new edit, by whoever returns, that makes the
  // page match it.
  restore(v: Version) {
    this.page.match(targetOf(this.at(v)), "edit");
  }
}

// ---------- a page as a file ----------

// The file is the page's text form, which reads like any markdown, with
// its whole history after it, so it opens anywhere with every version.
export function fileOf(page: Page, id: string): string {
  const snapshot = page.doc.export({ mode: "snapshot" });
  return `${print(page.roots(), page.tokens.toJSON() as Record<string, string>)}\n<!-- editor page ${id} -->\n<!-- history ${base64(snapshot)} -->\n`;
}

export type Opened = { id?: string; snapshot?: Uint8Array; target: Target };

export function readFile(text: string): Opened {
  const id = text.match(/^<!-- editor page ([a-z0-9-]{4,64}) -->$/im)?.[1];
  const b64 = text.match(/^<!-- history ([A-Za-z0-9+/=]+) -->$/m)?.[1];
  const target = parse(text);
  return { id, snapshot: b64 ? unbase64(b64) : undefined, target };
}

// A page opened from a file: its history if it has one, else its text.
export function pageFrom(opened: Opened): Page {
  const page = new Page(new LoroDoc());
  if (opened.snapshot) page.doc.import(opened.snapshot);
  else page.seed(opened.target.nodes, opened.target.tokens);
  return page;
}

function base64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function unbase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
